<template>
  <div class="page">
    <!-- HEADER -->
    <div class="page-head">
      <div>
        <h1>Audit Logs</h1>
      </div>

      <div class="actions">
        <div class="search-mini">
          <i class="bx bx-search"></i>
          <input
            v-model="query"
            placeholder="Search userId, employee, entityId, IP, keyword..."
            @keyup.enter="fetchLogs"
          />
        </div>

        <button class="btn ghost" @click="toggleFilters">
          <i class="bx bx-filter"></i>
          Filters
        </button>

        <button class="btn ghost" :disabled="loading.list" @click="exportXl">
          <i class="bx bx-export"></i>
          Export
        </button>

        <button class="btn primary" :disabled="loading.list" @click="refreshAll('Logs refreshed ✅')">
          <i v-if="!loading.list" class="bx bx-refresh"></i>
          <span v-else class="loader sm"></span>
          Refresh
        </button>
      </div>
    </div>

    <!-- SUMMARY -->
    <div class="grid cols-4" style="margin-top: 10px;">
      <div class="card stat">
        <div class="card-title">
          <strong>Today Logs</strong>
          <span class="badge primary">LIVE</span>
        </div>
        <div class="metric">
          <span v-if="loading.stats" class="loader"></span>
          <span v-else>{{ todayCount }}</span>
        </div>
        <div class="mini">Recorded events today</div>
      </div>

      <div class="card stat">
        <div class="card-title">
          <strong>Security Events</strong>
          <span class="badge warning">ALERT</span>
        </div>
        <div class="metric">
          <span v-if="loading.stats" class="loader"></span>
          <span v-else>{{ securityCount }}</span>
        </div>
        <div class="mini">Login / access related</div>
      </div>

      <div class="card stat">
        <div class="card-title">
          <strong>Finance Actions</strong>
          <span class="badge primary">PAY</span>
        </div>
        <div class="metric">
          <span v-if="loading.stats" class="loader"></span>
          <span v-else>{{ financeCount }}</span>
        </div>
        <div class="mini">Withdrawals & repayments</div>
      </div>

      <div class="card stat">
        <div class="card-title">
          <strong>Critical Changes</strong>
          <span class="badge warning">RISK</span>
        </div>
        <div class="metric">
          <span v-if="loading.stats" class="loader"></span>
          <span v-else>{{ criticalCount }}</span>
        </div>
        <div class="mini">Settings or admin changes</div>
      </div>
    </div>

    <!-- FILTER PANEL -->
    <div v-if="showFilters" class="card filters" v-motion-pop style="margin-top: 14px;">
      <div class="filters-head">
        <div>
          <strong>Filters</strong>
          <div class="row-muted">Narrow down logs to quickly locate problems.</div>
        </div>

        <div class="filters-actions">
          <button class="btn ghost" :disabled="loading.list" @click="resetFilters">
            <i class="bx bx-refresh"></i> Reset
          </button>

          <button class="btn primary" :disabled="loading.list" @click="applyFilters">
            <i v-if="!loading.list" class="bx bx-check"></i>
            <span v-else class="loader sm"></span>
            Apply
          </button>
        </div>
      </div>

      <div class="filters-grid">
        <Field label="From Date" icon="bx-calendar" type="date" v-model="fromDate" />
        <Field label="To Date" icon="bx-calendar-check" type="date" v-model="toDate" />

        <SelectField label="Module" icon="bx-grid-alt" v-model="module" :options="moduleOptions" />
        <SelectField label="Action" icon="bx-bolt-circle" v-model="action" :options="actionOptions" />

        <SelectField label="Employee" icon="bx-user" v-model="employee" :options="employeeOptions" />
        <SelectField label="Status" icon="bx-badge-check" v-model="status" :options="statusOptions" />
      </div>
    </div>

    <!-- TABLE -->
    <div class="card" style="margin-top: 14px;">
      <div class="table-head">
        <div class="table-title">
          <strong>Logs</strong>
          <span class="muted">Showing {{ filtered.length }} records</span>
        </div>

        <div class="quick">
          <span class="pill tiny primary" @click="setQuick('TODAY')">
            <i class="bx bx-sun"></i> Today
          </span>
          <span class="pill tiny" @click="setQuick('7D')">
            <i class="bx bx-calendar-week"></i> Last 7 days
          </span>
          <span class="pill tiny" @click="setQuick('ALL')">
            <i class="bx bx-infinite"></i> All
          </span>
        </div>
      </div>

      <!-- LOADING ROW -->
      <div v-if="loading.list" class="empty" style="padding: 22px;">
        <span class="loader big"></span>
        <div>
          <strong>Loading logs...</strong>
          <div class="row-muted">Fetching audit events from server</div>
        </div>
      </div>

      <!-- Desktop -->
      <div v-else class="table-wrap desktop-only" style="margin-top: 1rem;">
        <table class="table">
          <thead>
            <tr>
              <th>TIME</th>
              <th>MODULE</th>
              <th>ACTION</th>
              <th>EMPLOYEE</th>
              <th>ENTITY</th>
              <th>IP</th>
              <th>STATUS</th>
              <th style="text-align:right;">ACTIONS</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="l in filtered" :key="l.id">
              <td class="row-muted">{{ l.time }}</td>

              <td>
                <span class="pill tiny" :class="moduleClass(l.module)">
                  <i class="bx" :class="moduleIcon(l.module)"></i>
                  {{ l.module }}
                </span>
              </td>

              <td>
                <div class="action">
                  <strong>{{ l.action }}</strong>
                  <span class="row-muted">{{ l.note }}</span>
                </div>
              </td>

              <td>
                <div class="emp">
                  <div class="avatar">{{ initials(l.employee) }}</div>
                  <div class="emp-meta">
                    <div class="emp-name">{{ l.employee }}</div>
                    <div class="row-muted">{{ l.role }}</div>
                  </div>
                </div>
              </td>

              <td>
                <div class="entity">
                  <strong>{{ l.entityType }} • {{ l.entityId }}</strong>
                  <span class="row-muted">User #{{ l.userId }}</span>
                </div>
              </td>

              <td class="row-muted">{{ l.ip }}</td>

              <td>
                <span class="status-pill" :class="statusClass(l.status)">
                  <i class="bx" :class="statusIcon(l.status)"></i>
                  {{ l.status }}
                </span>
              </td>

              <td style="text-align:right;">
                <div class="row-actions">
                  <button class="icon-mini" @click="openDrawer(l)" title="Details">
                    <i class="bx bx-show"></i>
                  </button>
                  <button class="icon-mini" @click="copyText(l.entityId, 'Entity ID copied')">
                    <i class="bx bx-copy"></i>
                  </button>
                </div>
              </td>
            </tr>

            <tr v-if="filtered.length === 0">
              <td colspan="8" style="padding:18px;">
                <div class="empty">
                  <i class="bx bx-file-find"></i>
                  <div>
                    <strong>No logs found</strong>
                    <div class="row-muted">Try changing filters or search keywords.</div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile -->
      <div v-if="!loading.list" class="mobile-only list-cards">
        <div v-for="l in filtered" :key="l.id" class="log-card">
          <div class="top">
            <span class="pill tiny" :class="moduleClass(l.module)">
              <i class="bx" :class="moduleIcon(l.module)"></i>
              {{ l.module }}
            </span>

            <span class="status-pill" :class="statusClass(l.status)">
              <i class="bx" :class="statusIcon(l.status)"></i>
              {{ l.status }}
            </span>
          </div>

          <div class="mid">
            <div class="action">
              <strong>{{ l.action }}</strong>
              <span class="row-muted">{{ l.note }}</span>
            </div>

            <div class="meta">
              <div class="emp">
                <div class="avatar">{{ initials(l.employee) }}</div>
                <div>
                  <div class="emp-name">{{ l.employee }}</div>
                  <div class="row-muted">{{ l.role }}</div>
                </div>
              </div>

              <div class="row-muted">Entity: {{ l.entityType }} • {{ l.entityId }}</div>
              <div class="row-muted">User #{{ l.userId }} • IP {{ l.ip }}</div>
              <div class="row-muted">Time: {{ l.time }}</div>
            </div>
          </div>

          <div class="card-actions">
            <button class="btn ghost" @click="openDrawer(l)">
              <i class="bx bx-show"></i> Details
            </button>
            <button class="btn ghost" @click="copyText(l.entityId, 'Entity ID copied')">
              <i class="bx bx-copy"></i> Copy ID
            </button>
          </div>
        </div>

        <div v-if="filtered.length === 0" class="log-card">
          <div class="empty">
            <i class="bx bx-file-find"></i>
            <div>
              <strong>No logs found</strong>
              <div class="row-muted">Try changing filters or search keywords.</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- DETAILS DRAWER -->
    <div v-if="drawer.open" class="drawer-backdrop" @click.self="closeDrawer">
      <aside class="drawer" v-motion-pop>
        <div class="drawer-head">
          <div class="drawer-title">
            <strong>Audit Details</strong>
            <span class="row-muted">Full event record</span>
          </div>
          <button class="icon-mini" @click="closeDrawer"><i class="bx bx-x"></i></button>
        </div>

        <div class="drawer-body">
          <div class="detail-card">
            <div class="dtop">
              <div class="left">
                <span class="pill tiny" :class="moduleClass(drawer.log?.module)">
                  <i class="bx" :class="moduleIcon(drawer.log?.module)"></i>
                  {{ drawer.log?.module }}
                </span>
                <strong class="big">{{ drawer.log?.action }}</strong>
                <div class="row-muted">{{ drawer.log?.note }}</div>
              </div>

              <span class="status-pill" :class="statusClass(drawer.log?.status)">
                <i class="bx" :class="statusIcon(drawer.log?.status)"></i>
                {{ drawer.log?.status }}
              </span>
            </div>

            <div class="dgrid">
              <KV label="Time" :value="drawer.log?.time" icon="bx-time" />
              <KV label="Employee" :value="drawer.log?.employee" icon="bx-user" />
              <KV label="Role" :value="drawer.log?.role" icon="bx-id-card" />
              <KV label="IP Address" :value="drawer.log?.ip" icon="bx-world" />
              <KV label="Entity" :value="`${drawer.log?.entityType} • ${drawer.log?.entityId}`" icon="bx-cube" />
              <KV label="User ID" :value="`#${drawer.log?.userId}`" icon="bx-user-pin" />
            </div>

            <div class="divider"></div>

            <div class="payload">
              <div class="payload-head">
                <strong>Payload</strong>
                <div class="payload-actions">
                  <button class="btn ghost sm" @click="copyJson">
                    <i class="bx bx-copy"></i> Copy JSON
                  </button>
                  <button class="btn primary sm" @click="toast.info('Print later')">
                    <i class="bx bx-printer"></i> Print
                  </button>
                </div>
              </div>

              <pre class="json">{{ pretty(drawer.log?.payload || {}) }}</pre>

              <div class="mini tip">
                <i class="bx bx-info-circle"></i>
                This payload is stored for audit and compliance. Do not edit.
              </div>
            </div>
          </div>

          <div class="qa">
            <button class="btn ghost" @click="toast.info('Open employee profile later')">
              <i class="bx bx-user-circle"></i> Employee
            </button>
            <button class="btn ghost" @click="toast.info('Open entity details later')">
              <i class="bx bx-link-external"></i> Entity
            </button>
            <button class="btn primary" @click="toast.success('Flag saved ✅')">
              <i class="bx bx-flag"></i> Flag
            </button>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, onMounted } from "vue";
import { useToast } from "vue-toastification";

import { getAuditStats, getAuditLogs, getAuditLogById,exportAuditLogsXl  } from "@/apis/audicon";

const exportXl = async () => {
  try {
    const res = await exportAuditLogsXl({
      admin_id: admin_id.value,
      keyword: query.value || "",
      from_date: fromDate.value || "",
      to_date: toDate.value || "",
      module: module.value || "",
      action: action.value || "",
      employee: employee.value || "",
      status: status.value || "",
      sortBy: "NEWEST",
    });

    const blob = new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit_logs_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Export downloaded ✅");
  } catch (e) {
    toast.error("Export failed");
  }
};


const toast = useToast();

/* admin */
const ldata = ref(null);
const admin_id = ref("");

/* UI */
const query = ref("");
const showFilters = ref(false);

/* Filters */
const fromDate = ref("");
const toDate = ref("");
const module = ref("");
const action = ref("");
const employee = ref("");
const status = ref("");
const quick = ref("ALL");
const sortBy = ref("NEWEST");

/* load */
const loading = reactive({
  stats: false,
  list: false,
});

/* Options */
const moduleOptions = [
  { label: "All Modules", value: "" },
  { label: "Auth", value: "AUTH" },
  { label: "KYC", value: "KYC" },
  { label: "Withdrawals", value: "WITHDRAWALS" },
  { label: "Repayments", value: "REPAYMENTS" },
  { label: "System", value: "SYSTEM" },
  { label: "Employees", value: "EMPLOYEES" },
];

const actionOptions = [
  { label: "All Actions", value: "" },
  { label: "Login", value: "LOGIN" },
  { label: "Logout", value: "LOGOUT" },
  { label: "Approve", value: "APPROVE" },
  { label: "Reject", value: "REJECT" },
  { label: "Settle", value: "SETTLE" },
  { label: "Update", value: "UPDATE" },
  { label: "Create", value: "CREATE" },
];

const employeeOptions = [
  { label: "All Employees", value: "" },
  { label: "Finance (Neha)", value: "Finance (Neha)" },
  { label: "Ops (Sahil)", value: "Ops (Sahil)" },
  { label: "Support (Aman)", value: "Support (Aman)" },
  { label: "Super Admin", value: "Super Admin" },
];

const statusOptions = [
  { label: "All Status", value: "" },
  { label: "Success", value: "SUCCESS" },
  { label: "Warning", value: "WARNING" },
  { label: "Failed", value: "FAILED" },
];

/* data */
const logs = ref([]);

/* stats */
const stats = reactive({
  todayCount: 0,
  securityCount: 0,
  financeCount: 0,
  criticalCount: 0,
});

/* analytics */
const todayCount = computed(() => stats.todayCount);
const securityCount = computed(() => stats.securityCount);
const financeCount = computed(() => stats.financeCount);
const criticalCount = computed(() => stats.criticalCount);

/* backend load */
const fetchStats = async () => {
  try {
    loading.stats = true;
    const res = await getAuditStats(admin_id.value);

    if (!res || !res.status) {
      toast.error(res?.success || "Failed to load stats");
      return;
    }

    stats.todayCount = Number(res.today_count || 0);
    stats.securityCount = Number(res.security_count || 0);
    stats.financeCount = Number(res.finance_count || 0);
    stats.criticalCount = Number(res.critical_count || 0);
  } catch {
    toast.error("Request Failed");
  } finally {
    loading.stats = false;
  }
};

const fetchLogs = async () => {
  try {
    loading.list = true;

    const res = await getAuditLogs({
      admin_id: admin_id.value,
      page_no: 0,
      keyword: query.value || "",
      from_date: fromDate.value || "",
      to_date: toDate.value || "",
      module: module.value || "",
      action: action.value || "",
      employee: employee.value || "",
      status: status.value || "",
      quick: quick.value || "ALL",
      sortBy: sortBy.value || "NEWEST",
    });

    if (!res || !res.status) {
      toast.error(res?.success || "Failed to load logs");
      return;
    }

    logs.value = (res.records || []).map((x) => ({
      ...x,
      id: Number(x.id || 0),
      userId: Number(x.userId || 0),
      payload: x.payload || {},
    }));
  } catch {
    toast.error("Request Failed");
  } finally {
    loading.list = false;
  }
};

const refreshAll = async (msg = "") => {
  await fetchStats();
  await fetchLogs();
  if (msg) toast.success(msg);
};

/* Filtered list (client side for fast UX) */
const filtered = computed(() => {
  const q = query.value.toLowerCase();

  return logs.value.filter((l) => {
    const matchesQ =
      !q ||
      String(l.entityId || "").toLowerCase().includes(q) ||
      String(l.entityType || "").toLowerCase().includes(q) ||
      String(l.userId || "").includes(q) ||
      String(l.employee || "").toLowerCase().includes(q) ||
      String(l.role || "").toLowerCase().includes(q) ||
      String(l.ip || "").toLowerCase().includes(q) ||
      String(l.note || "").toLowerCase().includes(q) ||
      String(l.action || "").toLowerCase().includes(q);

    return matchesQ;
  });
});

/* Drawer */
const drawer = reactive({ open: false, log: null });

const openDrawer = async (l) => {
  try {
    drawer.open = true;
    drawer.log = l;

    // optional: fetch full data again
    const res = await getAuditLogById({
      admin_id: admin_id.value,
      ulog_id: String(l.id || ""),
    });

    if (res && res.status && res.log) {
      drawer.log = res.log;
    }
  } catch {
    console.log("sdfessed");
  }
};

const closeDrawer = () => {
  drawer.open = false;
  drawer.log = null;
};

/* UI handlers */
const toggleFilters = () => {
  showFilters.value = !showFilters.value;
};

const resetFilters = () => {
  fromDate.value = "";
  toDate.value = "";
  module.value = "";
  action.value = "";
  employee.value = "";
  status.value = "";
  quick.value = "ALL";
  sortBy.value = "NEWEST";
  toast.info("Filters reset");
};

const applyFilters = async () => {
  showFilters.value = false;
  await fetchLogs();
};

const setQuick = async (v) => {
  quick.value = v;
  await fetchLogs();

  if (v === "TODAY") toast.info("Showing today's logs");
  else if (v === "7D") toast.info("Showing last 7 days");
  else toast.info("Showing all logs");
};

/* Helpers */
const pretty = (obj) => JSON.stringify(obj, null, 2);

const initials = (name) => {
  if (!name) return "A";
  const parts = String(name).split(" ").filter(Boolean);
  return (parts[0]?.[0] || "A") + (parts[1]?.[0] || "");
};

const moduleClass = (m) => {
  if (m === "WITHDRAWALS") return "purple";
  if (m === "REPAYMENTS") return "primary";
  if (m === "KYC") return "cyan";
  if (m === "AUTH") return "warning";
  if (m === "SYSTEM") return "danger";
  if (m === "EMPLOYEES") return "success";
  return "primary";
};

const moduleIcon = (m) => {
  if (m === "WITHDRAWALS") return "bx-send";
  if (m === "REPAYMENTS") return "bx-receipt";
  if (m === "KYC") return "bx-id-card";
  if (m === "AUTH") return "bx-lock-alt";
  if (m === "SYSTEM") return "bx-cog";
  if (m === "EMPLOYEES") return "bx-group";
  return "bx-grid-alt";
};

const statusClass = (s) => {
  if (s === "SUCCESS") return "success";
  if (s === "WARNING") return "warning";
  if (s === "FAILED") return "danger";
  return "primary";
};

const statusIcon = (s) => {
  if (s === "SUCCESS") return "bx-check-circle";
  if (s === "WARNING") return "bx-error-circle";
  if (s === "FAILED") return "bx-x-circle";
  return "bx-info-circle";
};

const copyText = async (text, msg = "Copied") => {
  try {
    await navigator.clipboard.writeText(String(text || ""));
    toast.success(msg);
  } catch {
    toast.error("Copy failed");
  }
};

const copyJson = () => copyText(pretty(drawer.log?.payload || {}), "JSON copied");

/* mount */
onMounted(async () => {
  try {
    ldata.value = JSON.parse(sessionStorage.getItem("ldata") || "{}");
    admin_id.value = ldata.value?.email || "1";
    await refreshAll();
  } catch {
    console.log("sdfessed");
  }
});
</script>


<script>
/* Inline small components */
export default {
  components: {
    Field: {
      props: {
        label: String,
        icon: String,
        modelValue: [String, Number],
        type: { type: String, default: "text" },
      },
      emits: ["update:modelValue"],
      template: `
        <div class="field">
          <label>{{label}}</label>
          <div class="input">
            <i class="bx" :class="icon"></i>
            <input :type="type" :value="modelValue" @input="$emit('update:modelValue',$event.target.value)" />
          </div>
        </div>
      `,
    },
    SelectField: {
      props: { label: String, icon: String, modelValue: [String, Number], options: Array },
      emits: ["update:modelValue"],
      template: `
        <div class="field">
          <label>{{label}}</label>
          <div class="input">
            <i class="bx" :class="icon"></i>
            <select class="select full" :value="modelValue" @change="$emit('update:modelValue',$event.target.value)">
              <option v-for="o in options" :key="o.value" :value="o.value">{{o.label}}</option>
            </select>
          </div>
        </div>
      `,
    },
    KV: {
      props: { label: String, value: String, icon: String },
      template: `
        <div class="kv">
          <span class="k"><i class="bx" :class="icon"></i> {{label}}</span>
          <strong class="v">{{value || "-"}}</strong>
        </div>
      `,
    },
  },
};
</script>

<style scoped>
.actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.search-mini {
  height: 44px;
  position: relative;
  min-width: 280px;
}

.search-mini i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(148, 163, 184, 0.92);
  font-size: 18px;
}

.search-mini input {
  width: 100%;
  height: 44px;
  border-radius: 16px;
  border: 1px solid var(--line);
  padding: 0 12px 0 42px;
  background: rgba(255, 255, 255, 0.95);
  font-weight: var(--w-medium);
  font-size: 13px;
  outline: none;
}

.search-mini input:focus {
  border-color: rgba(46, 109, 231, 0.35);
  box-shadow: 0 0 0 4px rgba(46, 109, 231, 0.12);
}

.filters {
  padding: 16px;
}

.filters-head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
  margin-bottom: 12px;
}

.filters-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.filters-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
}

.quick {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.pill.tiny {
  cursor: pointer;
  width: fit-content;
  display: inline-flex;
  gap: 8px;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: var(--w-bold);
  background: rgba(241,245,249,0.65);
  border: 1px solid var(--line);
  color: rgba(15,23,42,0.78);
}

.pill.tiny i {
  font-size: 16px;
}

.pill.tiny.primary {
  background: var(--primary-10);
  color: var(--primary);
  border-color: rgba(46,109,231,0.18);
}

.action {
  display: grid;
  gap: 4px;
}

.action strong {
  font-weight: var(--w-black);
  letter-spacing: -0.2px;
}

.emp {
  display: flex;
  align-items: center;
  gap: 10px;
}

.emp-name {
  font-weight: var(--w-bold);
  font-size: 13px;
}

.entity {
  display: grid;
  gap: 4px;
}

.row-actions {
  display: inline-flex;
  gap: 8px;
  justify-content: flex-end;
}

.icon-mini {
  height: 38px;
  width: 38px;
  border-radius: 14px;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.96);
  display: grid;
  place-items: center;
  cursor: pointer;
}

.icon-mini i {
  font-size: 18px;
  color: rgba(11, 18, 32, 0.88);
}

.icon-mini:hover {
  transform: translateY(-1px);
}

.empty {
  display: flex;
  gap: 12px;
  align-items: center;
}

.empty i {
  font-size: 26px;
  color: rgba(148, 163, 184, 0.9);
}

/* module pills */
.pill.tiny.purple { background: rgba(124,58,237,0.10); border-color: rgba(124,58,237,0.18); color: rgba(124,58,237,0.95); }
.pill.tiny.cyan { background: rgba(8,145,178,0.10); border-color: rgba(8,145,178,0.20); color: rgba(8,145,178,0.95); }
.pill.tiny.warning { background: var(--warning-10); border-color: rgba(234,88,12,0.18); color: var(--warning); }
.pill.tiny.danger { background: rgba(239,68,68,0.10); border-color: rgba(239,68,68,0.20); color: var(--danger); }
.pill.tiny.success { background: var(--success-10); border-color: rgba(22,163,74,0.18); color: var(--success); }

/* mobile cards */
.list-cards {
  display: none;
  margin-top: 10px;
  gap: 12px;
}

.log-card {
  border: 1px solid var(--line);
  border-radius: var(--radius-xl);
  padding: 14px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: var(--shadow-sm);
}

.log-card .top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
}

.log-card .mid {
  margin-top: 10px;
  display: grid;
  gap: 10px;
}

.meta {
  display: grid;
  gap: 8px;
}

.card-actions {
  margin-top: 12px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

/* drawer */
.drawer-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.55);
  backdrop-filter: blur(8px);
  z-index: 90;
  display: flex;
  justify-content: flex-end;
}

.drawer {
  width: min(560px, 92vw);
  height: 100%;
  background: rgba(255, 255, 255, 0.95);
  border-left: 1px solid var(--line);
  box-shadow: var(--shadow);
  padding: 16px;
  overflow: auto;
}

.drawer-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--line);
}

.drawer-title strong {
  font-weight: var(--w-bold);
  font-size: 16px;
}

.drawer-body {
  margin-top: 14px;
  display: grid;
  gap: 14px;
}

.detail-card {
  border: 1px solid var(--line);
  border-radius: var(--radius-xl);
  background: rgba(246, 248, 252, 0.65);
  padding: 14px;
}

.dtop {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.dtop .big {
  display: block;
  margin-top: 10px;
  font-size: 18px;
  font-weight: var(--w-black);
  letter-spacing: -0.3px;
}

.dgrid {
  margin-top: 14px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.kv {
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.96);
  border-radius: 18px;
  padding: 10px;
}

.kv .k {
  font-size: 11px;
  color: var(--muted);
  font-weight: var(--w-medium);
  display: inline-flex;
  gap: 8px;
  align-items: center;
}

.kv .k i { font-size: 16px; color: rgba(100,116,139,0.9); }

.kv .v {
  margin-top: 6px;
  display: block;
  font-size: 13px;
  font-weight: var(--w-bold);
}

.divider {
  height: 1px;
  background: rgba(231, 238, 248, 0.9);
  margin: 14px 0;
}

.payload-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 10px;
}

.payload-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.btn.sm {
  padding: 10px 12px;
  border-radius: 14px;
  font-size: 12px;
}

.json {
  padding: 12px;
  border-radius: 18px;
  border: 1px solid var(--line);
  background: rgba(255,255,255,0.96);
  font-size: 12px;
  font-weight: var(--w-medium);
  color: rgba(15,23,42,0.8);
  overflow: auto;
}

.tip {
  margin-top: 10px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px;
  border-radius: 18px;
  background: rgba(46, 109, 231, 0.08);
  border: 1px solid rgba(46, 109, 231, 0.16);
  font-size: 12px;
}

.tip i {
  font-size: 18px;
  color: var(--primary);
  margin-top: 1px;
}

.qa {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.select {
  height: 44px;
  border-radius: 16px;
  border: 1px solid var(--line);
  padding: 0 12px;
  background: rgba(255, 255, 255, 0.95);
  font-weight: var(--w-medium);
  font-size: 13px;
  outline: none;
}

.select.full {
  width: 100%;
  height: 48px;
  border-radius: 18px;
  padding-left: 44px;
}

.field label {
  font-size: 12px;
  font-weight: var(--w-semibold);
  color: rgba(15, 23, 42, 0.78);
  margin-bottom: 7px;
  display: block;
}

.input {
  position: relative;
}

.input i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(148, 163, 184, 0.92);
  font-size: 18px;
}

.input input {
  width: 100%;
  height: 48px;
  padding: 0 14px 0 44px;
  border: 1px solid var(--line);
  border-radius: 18px;
  outline: none;
  background: rgba(255, 255, 255, 0.96);
  font-size: 13px;
  font-weight: var(--w-medium);
}

.input input:focus {
  border-color: rgba(46, 109, 231, 0.35);
  box-shadow: 0 0 0 4px rgba(46, 109, 231, 0.12);
}

@media (max-width: 980px) {
  .filters-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 960px) {
  .desktop-only { display: none; }
  .mobile-only { display: block; }
  .list-cards { display: grid; }
  .dgrid { grid-template-columns: 1fr; }
}
/* ===========================
   AUDIT LOGS - REQUIRED GLOBAL CSS
   (safe to include, no conflicts)
   =========================== */

.table-wrap {
  width: 100%;
  overflow: auto;
  border-radius: var(--radius-xl);
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th {
  text-align: left;
  font-size: 11px;
  letter-spacing: 1.5px;
  font-weight: 900;
  color: #94a3b8;
  padding: 12px 10px;
  border-bottom: 1px solid var(--line);
  white-space: nowrap;
}

.table td {
  padding: 14px 10px;
  font-weight: 700;
  font-size: 13px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: top;
}

.row-muted {
  color: #475569;
  font-weight: 600;
  font-size: 12px;
}

.avatar {
  height: 42px;
  width: 42px;
  border-radius: 999px;
  background: var(--primary-10);
  display: grid;
  place-items: center;
  color: var(--primary);
  font-weight: 900;
}

.badge {
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 11px;
  letter-spacing: 1px;
}

.badge.success {
  background: var(--success-10);
  color: var(--success);
}

.badge.warning {
  background: var(--warning-10);
  color: var(--warning);
}

.badge.primary {
  background: var(--primary-10);
  color: var(--primary);
}

.status-pill {
  padding: 7px 12px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 11px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.status-pill i {
  font-size: 16px;
}

.status-pill.success {
  background: var(--success-10);
  color: var(--success);
}

.status-pill.warning {
  background: var(--warning-10);
  color: var(--warning);
}

.status-pill.danger {
  background: var(--danger-10);
  color: var(--danger);
}

/* Buttons */
.btn {
  cursor: pointer;
  border: none;
  border-radius: 16px;
  padding: 12px 14px;
  font-weight: 900;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  justify-content: center;
}

.btn i {
  font-size: 18px;
}

.btn.primary {
  background: var(--primary);
  color: white;
  box-shadow: 0 18px 24px rgba(46, 109, 231, 0.22);
}

.btn.primary:hover {
  filter: brightness(0.97);
}

.btn.ghost {
  background: #f1f5f9;
  color: #334155;
}

.btn.ghost:hover {
  background: #e2e8f0;
}

/* Page basics */
.page {
  padding: 18px 20px 30px;
}

.page-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
}

.page-head h1 {
  font-size: 22px;
  font-weight: 900;
  letter-spacing: -0.3px;
}

.page-head p {
  font-size: 13px;
  font-weight: 600;
  color: var(--muted);
  margin-top: 6px;
}

.metric {
  font-size: 26px;
  font-weight: 900;
  letter-spacing: -0.5px;
}

/* Grid + Cards */
.grid {
  display: grid;
  gap: 14px;
}

.grid.cols-4 {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.card {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: var(--radius-xl);
  padding: 16px;
  box-shadow: var(--shadow-sm);
}

.card-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.card-title strong {
  font-size: 14px;
  font-weight: 900;
}

/* Responsive */
@media (max-width: 980px) {
  .grid.cols-4 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
/* ✅ FIX ALIGNMENT + SPACING + REDUCE FONT WEIGHT (Audit Logs) */

/* Page spacing */
.page {
  padding: 16px 18px 28px;
}

/* Header alignment */
.page-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.page-head h1 {
  font-size: 22px;
  font-weight: 800; /* ⬇ reduced */
  letter-spacing: -0.25px;
  margin: 0;
}

.page-head p {
  font-size: 13px;
  font-weight: 600; /* ⬇ reduced */
  color: var(--muted);
  margin: 6px 0 0;
  line-height: 1.45;
}

/* Actions row */
.actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
}

/* Search */
.search-mini {
  height: 44px;
  position: relative;
  min-width: 320px;
}

.search-mini i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(148, 163, 184, 0.92);
  font-size: 18px;
}

.search-mini input {
  width: 100%;
  height: 44px;
  border-radius: 16px;
  border: 1px solid var(--line);
  padding: 0 12px 0 42px;
  background: rgba(255, 255, 255, 0.95);
  font-weight: 600; /* ⬇ reduced */
  font-size: 13px;
  outline: none;
}

.search-mini input:focus {
  border-color: rgba(46, 109, 231, 0.35);
  box-shadow: 0 0 0 4px rgba(46, 109, 231, 0.12);
}

/* Grid */
.grid {
  display: grid;
  gap: 12px; /* tighter */
}

.grid.cols-4 {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

/* Cards */
.card {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: var(--radius-xl);
  padding: 14px; /* tighter */
  box-shadow: var(--shadow-sm);
}

.card-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.card-title strong {
  font-size: 13px;
  font-weight: 700; /* ⬇ reduced */
}

/* Metrics */
.metric {
  font-size: 24px;
  font-weight: 800; /* ⬇ reduced */
  letter-spacing: -0.3px;
  line-height: 1.1;
}

.mini {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600; /* ⬇ reduced */
  color: var(--muted);
}

/* Table head */
.table-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.table-title {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.table-title strong {
  font-weight: 800; /* ⬇ reduced */
  font-size: 15px;
}

.muted {
  color: var(--muted);
  font-size: 12px;
  font-weight: 600; /* ⬇ reduced */
}

/* Quick pills */
.quick {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.pill.tiny {
  cursor: pointer;
  display: inline-flex;
  gap: 8px;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700; /* ⬇ reduced */
  background: rgba(241, 245, 249, 0.65);
  border: 1px solid var(--line);
  color: rgba(15, 23, 42, 0.78);
}

.pill.tiny i {
  font-size: 15px;
}

/* Table */
.table-wrap {
  width: 100%;
  overflow: auto;
  border-radius: var(--radius-xl);
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th {
  text-align: left;
  font-size: 11px;
  letter-spacing: 1.4px;
  font-weight: 800; /* ⬇ reduced */
  color: #94a3b8;
  padding: 12px 10px;
  border-bottom: 1px solid var(--line);
  white-space: nowrap;
}

.table td {
  padding: 14px 10px;
  font-weight: 600; /* ⬇ reduced */
  font-size: 13px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: top;
}

/* Cell blocks */
.action {
  display: grid;
  gap: 4px;
}

.action strong {
  font-weight: 700; /* ⬇ reduced */
  letter-spacing: -0.1px;
}

.row-muted {
  color: #475569;
  font-weight: 600; /* ⬇ reduced */
  font-size: 12px;
  line-height: 1.35;
}

/* Employee block */
.emp {
  display: flex;
  align-items: center;
  gap: 10px;
}

.avatar {
  height: 40px;
  width: 40px;
  border-radius: 999px;
  background: var(--primary-10);
  display: grid;
  place-items: center;
  color: var(--primary);
  font-weight: 800; /* ⬇ reduced */
  flex: 0 0 auto;
}

.emp-meta {
  display: grid;
  gap: 2px;
}

.emp-name {
  font-weight: 700; /* ⬇ reduced */
  font-size: 13px;
}

/* Entity block */
.entity {
  display: grid;
  gap: 4px;
}

.entity strong {
  font-weight: 700; /* ⬇ reduced */
}

/* Status pill */
.status-pill {
  padding: 7px 12px;
  border-radius: 999px;
  font-weight: 800; /* ⬇ reduced */
  font-size: 11px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.status-pill i {
  font-size: 16px;
}

/* Actions icons */
.row-actions {
  display: inline-flex;
  gap: 8px;
  justify-content: flex-end;
}

.icon-mini {
  height: 36px;
  width: 36px;
  border-radius: 14px;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.96);
  display: grid;
  place-items: center;
  cursor: pointer;
}

.icon-mini i {
  font-size: 18px;
  color: rgba(11, 18, 32, 0.88);
}

/* Filters */
.filters {
  padding: 14px;
}

.filters-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.filters-head strong {
  font-weight: 800; /* ⬇ reduced */
}

.filters-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.filters-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
}

/* Drawer alignment */
.drawer {
  width: min(560px, 92vw);
  height: 100%;
  background: rgba(255, 255, 255, 0.95);
  border-left: 1px solid var(--line);
  box-shadow: var(--shadow);
  padding: 14px;
  overflow: auto;
}

.drawer-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--line);
}

.drawer-title strong {
  font-weight: 800; /* ⬇ reduced */
  font-size: 15px;
}

/* Responsive */
@media (max-width: 1100px) {
  .grid.cols-4 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 980px) {
  .search-mini {
    min-width: 240px;
    flex: 1;
  }
  .filters-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 960px) {
  .desktop-only {
    display: none;
  }
  .mobile-only {
    display: block;
  }
  .list-cards {
    display: grid;
    gap: 12px;
  }
  .dgrid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 520px) {
  .page {
    padding: 14px 14px 24px;
  }
  .actions {
    justify-content: flex-start;
  }
}

</style>
